import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Crown, Coins, Check, ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { track } from "@/lib/analytics";
import { isNativeApp } from "@/lib/platform";
import { toast } from "sonner";

export default function SubscriptionChoice() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, setUser, refresh } = useAuth();
  const [busy, setBusy] = useState(null); // 'tokens' | 'premium'
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api.get("/subscription/plan").then((r) => setPlan(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (params.get("canceled") === "1") {
      toast.info("Paiement annulé — vous pouvez réessayer ou choisir les jetons.");
    }
  }, [params]);

  const priceLabel = plan
    ? `${plan.amount.toString().replace(".", ",")} €/mois`
    : "9,99 €/mois";

  const chooseTokens = async () => {
    setBusy("tokens");
    track("pro_plan_tokens_chosen", { source: "onboarding" });
    try {
      const { data } = await api.post("/onboarding/start-tokens");
      setUser(data.user);
      toast.success("Bienvenue ! Vous pouvez explorer les demandes locales.");
      nav("/pro/home", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const choosePremium = async () => {
    setBusy("premium");
    track("pro_plan_premium_chosen", { source: "onboarding" });
    try {
      const { data } = await api.post("/subscription/mollie/checkout", {
        origin_url: window.location.origin,
      });
      track("mollie_checkout_started", { mode: data.mode, payment_id: data.payment_id });
      // Redirect to Mollie (or mock simulation page)
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(null);
    }
  };

  if (!user) {
    // Not authenticated: send them back to onboarding.
    refresh?.();
    return null;
  }

  const native = isNativeApp();

  return (
    <div className="app-shell grain-bg flex flex-col">
      <header className="px-5 py-5 border-b border-border">
        <div className="text-[10px] uppercase tracking-widest text-slate2">Étape finale</div>
        <h1 className="font-heading font-bold text-2xl text-ink mt-1">Choisissez votre formule</h1>
        <p className="text-sm text-slate2 mt-2 leading-relaxed">
          Comment souhaitez-vous utiliser Besoin d&apos;un Pro&nbsp;?
        </p>
      </header>

      <div className="px-5 py-5 space-y-4 pb-24">
        {/* Option 1 — Tokens */}
        <div className="rounded-2xl bg-white border-2 border-border p-5" data-testid="plan-tokens-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-9 w-9 rounded-xl bg-brand-light text-brand flex items-center justify-center">
              <Coins className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold text-lg text-ink">Paiement par lead</div>
              <div className="text-[11px] uppercase tracking-wider text-slate2">Recommandé pour commencer</div>
            </div>
          </div>
          <ul className="text-sm text-ink space-y-1.5 mb-4 mt-3">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-brand mt-0.5 shrink-0" /> Vous ne payez qu&apos;au moment de débloquer un contact</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-brand mt-0.5 shrink-0" /> Aucun abonnement, aucun engagement</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-brand mt-0.5 shrink-0" /> Vous pouvez passer au Premium à tout moment</li>
          </ul>
          <Button
            onClick={chooseTokens}
            disabled={!!busy}
            className="w-full rounded-full bg-white border-2 border-brand text-brand hover:bg-brand-light py-6 text-base font-semibold"
            data-testid="plan-tokens-btn"
          >
            {busy === "tokens" ? "Activation…" : "Commencer avec les jetons"}
          </Button>
        </div>

        {/* Option 2 — Premium — masqué sur mobile natif tant que Google Play Billing n'est pas branché
             (Google Play interdit les paiements de biens numériques par une autre méthode). */}
        {!native && (
        <div className="rounded-2xl bg-pro text-white p-5 relative overflow-hidden" data-testid="plan-premium-card">
          <div className="absolute top-3 right-3 bg-brand text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full">
            Populaire
          </div>
          <div className="flex items-center gap-2 mb-2">
            <div className="h-9 w-9 rounded-xl bg-white/15 flex items-center justify-center">
              <Crown className="w-5 h-5 text-brand" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg">Premium</div>
              <div className="text-[11px] uppercase tracking-wider text-white/70" data-testid="plan-premium-price">{priceLabel}</div>
            </div>
          </div>
          <ul className="text-sm text-white/90 space-y-1.5 mb-4 mt-3">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-brand mt-0.5 shrink-0" /> Accès illimité aux demandes locales</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-brand mt-0.5 shrink-0" /> IA Pro : rédaction de devis, coach business</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-brand mt-0.5 shrink-0" /> Priorité sur les nouveaux leads</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-brand mt-0.5 shrink-0" /> Badge Premium visible sur votre profil</li>
          </ul>
          <Button
            onClick={choosePremium}
            disabled={!!busy}
            className="w-full rounded-full bg-white text-pro hover:bg-white/90 py-6 text-base font-semibold"
            data-testid="plan-premium-btn"
          >
            {busy === "premium" ? "Redirection…" : (
              <>Souscrire maintenant <ArrowRight className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </div>
        )}

        {native && (
          <div className="rounded-2xl bg-pro/5 border border-pro/20 p-4 text-xs text-slate2 flex items-start gap-2" data-testid="premium-android-notice">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-pro" />
            L&apos;abonnement Premium sera bientôt disponible dans l&apos;application Android via Google Play. Commencez dès maintenant avec les jetons.
          </div>
        )}

        <p className="text-center text-[11px] text-slate2 pt-2">
          Vous pourrez changer de formule à tout moment depuis votre profil.
        </p>
      </div>
    </div>
  );
}
